import { useCallback, useEffect, useRef, useState } from 'react'
import {
  PHONE_LAYOUT_QUERY,
  SWIPE_EXIT_MS,
  SWIPE_SNAP_MS,
  isSwipeInteractiveTarget,
  resolveSwipeAxis,
  shouldCommitSwipe,
  swipeFollowOpacity,
  swipeExitX,
  swipePointerAllowed,
} from '../utils/notificationSwipe'

function prefersPhoneLayout() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia(PHONE_LAYOUT_QUERY).matches
}

export function useNotificationSwipe({
  enabled = true,
  reducedMotion = false,
  onDismiss,
  onInteractChange,
} = {}) {
  const nodeRef = useRef(null)
  const sessionRef = useRef(null)
  const frameRef = useRef(0)
  const interactRef = useRef(false)
  const dismissRef = useRef(onDismiss)
  const suppressClickUntilRef = useRef(0)
  dismissRef.current = onDismiss
  const [visual, setVisual] = useState({ x: 0, opacity: 1, phase: 'idle' })

  const setInteract = useCallback((next) => {
    if (interactRef.current === next) return
    interactRef.current = next
    onInteractChange?.(next)
  }, [onInteractChange])

  const resetVisual = useCallback(() => {
    setVisual({ x: 0, opacity: 1, phase: 'idle' })
  }, [])

  useEffect(() => {
    const node = nodeRef.current
    if (!node || !enabled) return undefined

    const finishSnap = () => {
      sessionRef.current = null
      setInteract(false)
      resetVisual()
    }

    const markSuppressClick = () => {
      suppressClickUntilRef.current = Date.now() + 400
    }

    const commitExit = (dx, width) => {
      const target = reducedMotion ? 0 : swipeExitX(dx, width)
      markSuppressClick()
      sessionRef.current = { locked: 'horizontal', committed: true, suppressClick: true }
      setInteract(false)
      if (reducedMotion) {
        dismissRef.current?.()
        resetVisual()
        return
      }
      setVisual({ x: target, opacity: 0, phase: 'exiting' })
      window.setTimeout(() => {
        dismissRef.current?.()
      }, SWIPE_EXIT_MS)
    }

    const onPointerDown = (event) => {
      if (!swipePointerAllowed(event.pointerType, prefersPhoneLayout())) return
      if (event.pointerType === 'mouse' && event.button !== 0) return
      if (sessionRef.current?.committed) return
      if (isSwipeInteractiveTarget(event.target)) return

      sessionRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastT: event.timeStamp,
        velocityX: 0,
        locked: 'undecided',
        committed: false,
        suppressClick: false,
      }
    }

    const onPointerMove = (event) => {
      const session = sessionRef.current
      if (!session || session.pointerId !== event.pointerId || session.committed) return

      const dx = event.clientX - session.startX
      const dy = event.clientY - session.startY
      const dt = Math.max(1, event.timeStamp - session.lastT)
      session.velocityX = (event.clientX - session.lastX) / dt
      session.lastX = event.clientX
      session.lastT = event.timeStamp

      if (session.locked === 'undecided') {
        const axis = resolveSwipeAxis({ dx, dy })
        if (axis === 'undecided') return
        if (axis !== 'horizontal') {
          sessionRef.current = null
          setInteract(false)
          return
        }
        session.locked = 'horizontal'
        session.suppressClick = true
        markSuppressClick()
        setInteract(true)
        if (event.pointerType !== 'mouse') {
          try {
            node.setPointerCapture(event.pointerId)
          } catch {
            // Capture optionnelle : le geste continue sans elle.
          }
        }
      }

      if (session.locked !== 'horizontal') return
      event.preventDefault()
      const width = node.getBoundingClientRect().width
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
      frameRef.current = requestAnimationFrame(() => {
        if (reducedMotion) {
          setVisual({ x: 0, opacity: 1, phase: 'dragging' })
          return
        }
        setVisual({
          x: dx,
          opacity: swipeFollowOpacity(dx, width),
          phase: 'dragging',
        })
      })
    }

    const endGesture = (event, cancelled) => {
      const session = sessionRef.current
      if (!session || session.committed) return
      if (session.pointerId !== event.pointerId && session.locked !== 'horizontal') return
      if (session.locked !== 'horizontal') {
        sessionRef.current = null
        setInteract(false)
        resetVisual()
        return
      }

      const dx = event.clientX - session.startX
      const width = node.getBoundingClientRect().width
      const commit = !cancelled && shouldCommitSwipe({
        dx,
        width,
        velocityX: session.velocityX,
      })

      if (node.hasPointerCapture?.(event.pointerId)) {
        try {
          node.releasePointerCapture(event.pointerId)
        } catch {
          // Déjà libérée.
        }
      }

      if (commit) {
        commitExit(dx, width)
        return
      }

      sessionRef.current = { ...session, locked: 'horizontal', committed: false }
      setInteract(false)
      if (reducedMotion) {
        finishSnap()
        return
      }
      setVisual({ x: 0, opacity: 1, phase: 'snap' })
      window.setTimeout(finishSnap, SWIPE_SNAP_MS)
    }

    const onClickCapture = (event) => {
      if (!sessionRef.current?.suppressClick && Date.now() >= suppressClickUntilRef.current) return
      event.preventDefault()
      event.stopPropagation()
    }

    const onPointerUp = (event) => endGesture(event, false)
    const onPointerCancel = (event) => endGesture(event, true)

    node.addEventListener('pointerdown', onPointerDown)
    node.addEventListener('pointermove', onPointerMove)
    node.addEventListener('pointerup', onPointerUp)
    node.addEventListener('pointercancel', onPointerCancel)
    node.addEventListener('click', onClickCapture, true)

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
      node.removeEventListener('pointerdown', onPointerDown)
      node.removeEventListener('pointermove', onPointerMove)
      node.removeEventListener('pointerup', onPointerUp)
      node.removeEventListener('pointercancel', onPointerCancel)
      node.removeEventListener('click', onClickCapture, true)
      setInteract(false)
    }
  }, [enabled, reducedMotion, resetVisual, setInteract])

  const dragging = visual.phase === 'dragging'
  const exiting = visual.phase === 'exiting'
  const snapping = visual.phase === 'snap'
  const duration = exiting ? SWIPE_EXIT_MS : snapping ? SWIPE_SNAP_MS : 0

  return {
    nodeRef,
    reset: resetVisual,
    dragging,
    exiting,
    style: {
      transform: visual.x ? `translate3d(${visual.x}px, 0, 0)` : undefined,
      opacity: visual.opacity,
      transition: duration
        ? `transform ${duration}ms ease, opacity ${duration}ms ease`
        : 'none',
      touchAction: 'pan-y',
    },
    className: [
      'ntoast-swipe',
      dragging ? 'is-dragging' : '',
      exiting ? 'is-exiting' : '',
      snapping ? 'is-snap' : '',
    ].filter(Boolean).join(' '),
  }
}
